import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cancelTranslation, getTranslation, retryTranslation, type Translation } from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { aroundAmount } from "./money";
import { headline, pauseReason } from "./translation";

/**
 * TranslationBanner is a translated book's run, on its studio page
 * (requirement 0018): how far it has got and what it has cost, why it paused
 * with Retry, Stop, and the pictures whose place was a guess. Nothing for a
 * book that is not a translation.
 *
 * While the run goes the banner polls, and each finished chapter refreshes the
 * chapter list, so chapters appear as they are written.
 */
export function useTranslation(bookID: string) {
  return useQuery({
    queryKey: ["translation", bookID],
    queryFn: () => getTranslation(bookID),
    refetchInterval: (query) => (query.state.data?.status === "running" ? 4000 : false),
  });
}

export default function TranslationBanner({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const translation = useTranslation(bookID);
  const tr = translation.data;

  const done = tr?.chapters_done;
  const status = tr?.status;
  useEffect(() => {
    if (done === undefined) return;
    void client.invalidateQueries({ queryKey: ["chapters", bookID] });
    void client.invalidateQueries({ queryKey: ["book", bookID] });
    void client.invalidateQueries({ queryKey: ["books"] });
  }, [client, bookID, done, status]);

  const settle = (next: Translation) => client.setQueryData(["translation", bookID], next);
  const retry = useMutation({ mutationFn: () => retryTranslation(bookID), onSuccess: settle });
  const cancel = useMutation({ mutationFn: () => cancelTranslation(bookID), onSuccess: settle });
  const [confirming, setConfirming] = useState(false);

  if (!tr) return null;
  const held = tr.status === "running" || tr.status === "paused";
  const [beforeSpent, afterSpent] = aroundAmount(
    held ? t.translation.spent : t.translation.spentTotal,
  );
  const estimated = tr.estimated_pictures.reduce((sum, c) => sum + c.picture_ids.length, 0);

  return (
    <section
      data-testid="translation-banner"
      data-status={tr.status}
      className={`mb-6 rounded-card border p-3 text-sm ${
        tr.status === "paused"
          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
          : "border-line bg-sunk"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium">
          {tr.status === "done" ? (
            <Link to={`/studio/books/${tr.source_book_id}`} className="hover:text-sage-deep">
              {/* The source title is authored text inside a sentence. */}
              {aroundTitle(t.translation.done, tr.source_title)}
            </Link>
          ) : (
            headline(tr, t)
          )}
        </p>
        <p data-testid="translation-spent" className="shrink-0 text-xs text-muted">
          {beforeSpent}
          <bdi dir="ltr">{tr.spent}</bdi>
          {afterSpent}
        </p>
      </div>

      {tr.status === "running" && (
        <p className="mt-1 text-xs text-muted">{t.translation.background}</p>
      )}
      {tr.status === "paused" && (
        <p
          data-testid="translation-reason"
          role="alert"
          className="mt-1 text-amber-900 dark:text-amber-200"
        >
          {pauseReason(tr, t)}
        </p>
      )}

      {estimated > 0 && (
        <div data-testid="translation-estimated" className="mt-2 text-xs text-muted">
          <p>{t.translation.estimatedPictures(estimated)}</p>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {tr.estimated_pictures.map((c) => (
              <li key={c.chapter_id}>
                <Link
                  to={`/studio/chapters/${c.chapter_id}`}
                  dir="auto"
                  className="inline-block rounded-full border border-line px-2.5 py-1 hover:bg-surface"
                >
                  {c.chapter_title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {held && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tr.status === "paused" && (
            <button
              type="button"
              data-testid="translation-retry"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
              className="rounded-full bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-50"
            >
              {t.translation.retry}
            </button>
          )}
          {confirming ? (
            <>
              <span className="self-center text-xs">{t.translation.cancelQuestion}</span>
              <button
                type="button"
                data-testid="translation-cancel-confirm"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(undefined, { onSettled: () => setConfirming(false) })}
                className="rounded-full border border-red-300 px-4 py-2 text-sm text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
              >
                {t.translation.cancel}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-full border border-line px-4 py-2 text-sm hover:bg-surface"
              >
                {t.common.keep}
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="translation-cancel"
              onClick={() => setConfirming(true)}
              className="rounded-full border border-line px-4 py-2 text-sm text-ink-soft transition-colors hover:bg-surface"
            >
              {t.translation.cancel}
            </button>
          )}
        </div>
      )}
      <MutationError className="mt-2" error={retry.error} fallback={t.translation.retryFailed} />
      <MutationError className="mt-2" error={cancel.error} fallback={t.translation.cancelFailed} />
    </section>
  );
}

/** aroundTitle puts an authored title inside a sentence, isolated so its script keeps its own direction. */
function aroundTitle(sentence: (title: string) => string, title: string) {
  const [before, after] = aroundAmount(sentence);
  return (
    <>
      {before}
      <bdi>{title}</bdi>
      {after}
    </>
  );
}
